import { useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { LegalTabs } from "./LegalTabs";
import { SelectField } from "@/components/Field";
import { useT } from "@/contexts/I18nContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { CALLABLES } from "@/services/callables";
import { TYPE_KEY, type DocType } from "./documents";
import {
  CELL_KEY,
  CELL_MARK,
  cellOf,
  countriesIn,
  gapsIn,
  isSound,
  type MatrixRow,
  type Preview,
} from "./matrix";
import type { Country, Segment } from "@/types";

/**
 * Country × segment → documents.
 *
 * Read-only, and fetched rather than re-implemented: the rows come from the
 * same function the session gate calls. A console that computed this itself
 * would be showing what the console believes, and the difference between that
 * and what the backend enforces is an institution teaching without an AVV.
 *
 * The preview underneath is the point of the screen. This is the single
 * highest-risk configuration in the system, and the only way it ever gets
 * verified is somebody choosing a profile they know the answer for and reading
 * what comes back.
 */
export function MatrixPage() {
  const t = useT();

  const matrix = useCallable<{ types: DocType[]; rows: MatrixRow[] }>(CALLABLES.getLegalMatrix);

  const [country, setCountry] = useState<Country>("DE");
  const [segment, setSegment] = useState<Segment>("school");

  const preview = useCallable<Preview, { country: Country; segment: Segment }>(
    CALLABLES.previewDocumentSet,
    { country, segment }
  );

  const rows = matrix.data?.rows ?? [];
  const types = matrix.data?.types ?? [];
  const countries = countriesIn(rows);

  return (
    <SectionShell titleKey="legal.matrix.title">
      <LegalTabs />

      <p className="max-w-3xl text-sm text-ink-soft">{t("legal.matrix.intro")}</p>
      <p className="mt-2 max-w-3xl text-sm text-ink-muted">{t("legal.matrix.readOnly")}</p>

      <section className="mt-8">
        <h2 className="font-display text-lg text-ink">{t("legal.matrix.preview")}</h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-muted">{t("legal.matrix.previewIntro")}</p>

        <div className="mt-4 flex flex-wrap gap-4">
          <SelectField
            labelKey="legal.matrix.country"
            value={country}
            onChange={(event) => setCountry(event.target.value as Country)}
            options={countries.map((candidate) => ({ value: candidate, labelKey: candidate }))}
          />
          <SelectField
            labelKey="legal.matrix.segment"
            value={segment}
            onChange={(event) => setSegment(event.target.value as Segment)}
            options={[
              { value: "school", labelKey: "institutions.segment.school" },
              { value: "studio", labelKey: "institutions.segment.studio" },
            ]}
          />
        </div>

        {preview.loading ? (
          <p className="mt-4 text-sm text-ink-muted">{t("common.loading")}</p>
        ) : preview.failed || !preview.data ? (
          <p className="mt-4 text-sm text-accent">{t("error.network.unavailable")}</p>
        ) : (
          <div className="mt-5 rounded-md border border-paper-deep bg-paper px-5 py-4">
            <p className="text-sm text-ink">
              {t("legal.matrix.resolvedFor", {
                country: preview.data.country,
                segment: t(`institutions.segment.${preview.data.segment}`),
                count: preview.data.entries.length,
              })}
            </p>

            <ul className="mt-3 flex flex-col gap-1.5">
              {preview.data.entries.map((entry) => (
                <li key={entry.type} className="flex flex-wrap items-baseline gap-x-3 text-sm">
                  <span className="text-ink">{t(TYPE_KEY[entry.type])}</span>
                  <span className="text-ink-muted">
                    {t(entry.required ? "legal.matrix.required" : "legal.matrix.reference")}
                  </span>
                  <span className="text-ink-muted">{entry.language.toUpperCase()}</span>
                  {entry.docId ? (
                    <span className="text-cue-green">
                      {t("legal.matrix.publishedAs", {
                        docId: entry.docId,
                        version: entry.version ?? 0,
                      })}
                    </span>
                  ) : (
                    /*
                      A gap, not a tick. Something the resolver requires and
                      nobody has published — which would let an institution
                      through the gate on a document that does not exist.
                    */
                    <span className="text-accent">{t("legal.matrix.notPublished")}</span>
                  )}
                </li>
              ))}
            </ul>

            {preview.data.unexpected.length > 0 ? (
              <p className="mt-4 text-sm text-accent">
                {t("legal.matrix.unexpected", {
                  documents: preview.data.unexpected.map((entry) => entry.docId).join(", "),
                })}
              </p>
            ) : null}

            <p className="mt-4 text-sm text-ink-soft">
              {isSound(preview.data)
                ? t("legal.matrix.sound")
                : t("legal.matrix.unsound", { count: gapsIn(preview.data).length })}
            </p>
          </div>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-display text-lg text-ink">{t("legal.matrix.wholeTable")}</h2>

        {/*
          A legend, because the marks are shorthand and shorthand nobody
          explained is a table people guess at.
        */}
        <p className="mt-2 text-sm text-ink-muted">
          {t("legal.matrix.legend", {
            required: CELL_MARK.required,
            reference: CELL_MARK.reference,
            none: CELL_MARK.none,
          })}
        </p>

        {matrix.loading ? (
          <p className="mt-4 text-sm text-ink-muted">{t("common.loading")}</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">{t("legal.matrix.title")}</caption>
              <thead>
                <tr className="border-b border-paper-deep text-left text-ink-muted">
                  <th scope="col" className="px-3 py-2.5 font-medium">
                    {t("legal.matrix.country")}
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-medium">
                    {t("legal.matrix.segment")}
                  </th>
                  {types.map((type) => (
                    <th key={type} scope="col" className="px-3 py-2.5 font-medium">
                      {t(TYPE_KEY[type])}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={`${row.country}-${row.segment}`}
                    className="border-b border-paper-warm last:border-0"
                  >
                    <td className="px-3 py-2.5 text-ink">{row.country}</td>
                    <td className="px-3 py-2.5 text-ink-soft">
                      {t(`institutions.segment.${row.segment}`)}
                    </td>
                    {types.map((type) => {
                      const cell = cellOf(row, type);
                      return (
                        <td key={type} className="px-3 py-2.5">
                          {/*
                            The mark is decoration; the label is the answer. A
                            table whose only signal is a symbol is a table
                            nobody can read aloud.
                          */}
                          <span aria-hidden="true" className="mr-1.5 text-ink-muted">
                            {CELL_MARK[cell]}
                          </span>
                          <span className="sr-only">{t(CELL_KEY[cell])}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </SectionShell>
  );
}
